/**
 * useFormContainer Hook
 * Main hook for managing FormContainer state and lifecycle
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { FormContainerConfig } from '../types/index';
import {
  setupFormContainerServices,
  cleanupFormContainerServices,
} from '../stores/serviceRegistry';
import { useMasterDataStore } from '../stores/MasterDataStore';
import { useChildFormRegistryStore, childFormKey } from '../stores/ChildFormRegistryStore';
import { useEventManagerStore } from '../stores/EventManagerStore';
import { ValidationStrategyFactory, validateChildForm } from '../strategies/ValidationStrategies';

/**
 * Ask a child form to show its own field errors. Works whether or not the child is mounted:
 * a child that mounts later picks up the request when it appears.
 */
function revealChildErrors(scope: string, formID: string): void {
  useChildFormRegistryStore.getState().bumpValidation(childFormKey(scope, formID));
}

export interface UseFormContainerReturn {
  activeIndex: number;
  isLoading: boolean;
  isSaving: boolean;
  errors: Record<string, any[]>;
  handleTabChange: (index: number) => Promise<void>;
  handleSave: () => Promise<void>;
  handleReset: () => void;
  getAllData: () => Record<string, unknown>;
}

/**
 * Main hook for FormContainer
 * Manages:
 * - Service setup/cleanup
 * - Active tab state
 * - Loading and saving states
 * - Validation errors
 */
export function useFormContainer(
  config: FormContainerConfig,
  initialData: Record<string, unknown> = {}
): UseFormContainerReturn {
  const childForms = config.childForms ?? [];
  const [activeIndex, setActiveIndex] = useState(
    config.layoutConfig?.defaultActiveIndex ?? 0
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, any[]>>({});

  // Access stores
  const masterData = useMasterDataStore();
  const childFormRegistry = useChildFormRegistryStore();
  const eventManager = useEventManagerStore();

  // Keep refs to config and initialData so event handlers always call the latest
  // version without needing to re-register listeners on every prop change.
  const configRef = useRef(config);
  configRef.current = config;

  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;

  // Initialize services and register event listeners
  useEffect(() => {
    setIsLoading(true);

    // Setup all services with latest initialData
    setupFormContainerServices(initialDataRef.current, childForms);

    // Listen for validation errors — reads from configRef to avoid stale closures
    const onValidationError = (event: any) => {
      if (event.formID && event.errors) {
        setErrors((prev) => ({
          ...prev,
          [event.formID]: event.errors,
        }));
        configRef.current.onValidationError?.(event.formID, event.errors);
      }
    };

    // Listen for data changes
    const onDataChanged = (event: any) => {
      configRef.current.onDataChange?.(event.formID, event.data);
    };

    eventManager.on('validation:error', onValidationError);
    eventManager.on('data:changed', onDataChanged);

    setIsLoading(false);

    // Cleanup only the listeners we registered — don't call cleanupFormContainerServices()
    // as it clears ALL listeners and breaks other FormContainers on the same page
    return () => {
      eventManager.off('validation:error', onValidationError);
      eventManager.off('data:changed', onDataChanged);
    };
  }, [childForms.length]);

  // Handle tab/step change with validation
  const handleTabChange = useCallback(
    async (newIndex: number) => {
      const scope = configRef.current.registryScope ?? '';
      const validationStrategy = ValidationStrategyFactory.createStrategy(
        configRef.current.validationMode,
        scope
      );

      // Check whether the child form being left is valid before moving forward.
      // Moving back to an earlier step is never blocked: the user can always return to fix a step.
      const currentFormID = childForms[activeIndex]?.formID;
      const movingBack = newIndex < activeIndex;
      let canSwitch = movingBack || (await validationStrategy.canSwitchForm(currentFormID));

      // "Validate before switch" applies to forward moves in every mode, not only Blocked Navigation
      if (canSwitch && !movingBack && currentFormID && configRef.current.validationConfig?.validateBeforeSwitch) {
        canSwitch = validateChildForm(scope, currentFormID).length === 0;
      }

      if (!canSwitch) {
        // Blocked: show the step's field errors so the user can see what to fix. A step that cannot
        // be checked yet reports that reason here, so its badge is not silently empty.
        if (currentFormID) {
          revealChildErrors(scope, currentFormID);
          const stepErrors = validateChildForm(scope, currentFormID);
          if (stepErrors.length > 0) {
            setErrors((prev) => ({ ...prev, [currentFormID]: stepErrors }));
          }
        }
        return;
      }

      // Call child form change callback
      const childForm = childForms[newIndex];
      if (childForm) {
        configRef.current.onChildFormChange?.(newIndex, childForm.formID);
      }

      setActiveIndex(newIndex);
    },
    [activeIndex, childForms]
  );

  // Handle save across all child forms
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const scope = configRef.current.registryScope ?? '';
      const validationStrategy = ValidationStrategyFactory.createStrategy(
        configRef.current.validationMode,
        scope
      );

      // Validate every child form through its registered engine
      const allData = useMasterDataStore.getState().getData();
      const childFormIDs = Object.fromEntries(
        (configRef.current.childForms ?? []).map((child) => [child.formID, {}])
      );
      const validationErrors = await validationStrategy.validateBeforeSave(
        childFormIDs
      );

      if (validationErrors && validationErrors.length > 0) {
        validationStrategy.onValidationError(validationErrors);

        // Show the field errors of every invalid step (mounted or not), and take the user to the first one
        const steps = configRef.current.childForms ?? [];
        let firstInvalid = -1;
        steps.forEach((child, index) => {
          if (validateChildForm(scope, child.formID).length === 0) return;
          revealChildErrors(scope, child.formID);
          if (firstInvalid < 0) firstInvalid = index;
        });
        if (firstInvalid >= 0) {
          setActiveIndex(firstInvalid);
          configRef.current.onChildFormChange?.(firstInvalid, steps[firstInvalid].formID);
        }
        return;
      }

      // Call onSubmit callback if provided
      if (configRef.current.onSubmit) {
        await configRef.current.onSubmit(allData);
      }

      // Emit save event
      useEventManagerStore.getState().emit('form:saved', { data: allData });
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Handle reset to initial data
  const handleReset = useCallback(() => {
    useMasterDataStore.getState().reset();
    setErrors({});
    setActiveIndex(configRef.current.layoutConfig?.defaultActiveIndex ?? 0);
  }, []);

  // Get all current data
  const getAllData = useCallback(() => {
    return useMasterDataStore.getState().getData();
  }, []);

  return {
    activeIndex,
    isLoading,
    isSaving,
    errors,
    handleTabChange,
    handleSave,
    handleReset,
    getAllData,
  };
}
